import React, { useState, useEffect } from 'react';
import {
  PencilIcon,
  TrashIcon,
  EyeIcon,
  EyeSlashIcon,
  StarIcon,
  MapPinIcon,
  ClockIcon,
  UsersIcon,
  CurrencyDollarIcon,
  TagIcon,
  CheckIcon
} from '@heroicons/react/24/outline';
import { useTheme } from '../../contexts/ThemeContext';
import { usePricing } from '../../contexts/PricingContext';
import { currencyService } from '../../services/currencyService';
import { PricingTier } from '../../types';

interface TourListProps {
  tours: any[];
  onEdit: (tour: any) => void;
  onDelete: (tourId: string) => void;
  onBulkDelete?: (tourIds: string[]) => void;
  onBulkUpdateStatus?: (tourIds: string[], status: 'active' | 'scheduled' | 'suspended' | 'deactivated' | 'expired') => void;
}

const TourList: React.FC<TourListProps> = ({ tours, onEdit, onDelete, onBulkDelete, onBulkUpdateStatus }) => {
  const [filter, setFilter] = useState('all');
  const [sortBy, setSortBy] = useState('featured'); // Default to featured order
  const [selectedTours, setSelectedTours] = useState<string[]>([]);
  const [selectAll, setSelectAll] = useState(false);
  const { actualTheme } = useTheme();
  const { displayCurrency, setCurrency, tier, setTier, exchangeRates } = usePricing();
  const [currencies, setCurrencies] = useState<string[]>([]);

  useEffect(() => {
    const loadCurrencies = async () => {
      const list = await currencyService.getSupportedCurrencies();
      setCurrencies(list);
    };
    loadCurrencies();
  }, []);

  const filteredTours = tours.filter(tour => {
    if (filter === 'all') return true;
    return tour.status === filter;
  });

  const sortedTours = sortBy === 'featured'
    ? filteredTours // Preserve the order from props when 'featured' is selected
    : [...filteredTours].sort((a, b) => {
      switch (sortBy) {
        case 'title':
          return a.title.localeCompare(b.title);
        case 'price':
          return b.price - a.price;
        case 'rating':
          return b.rating - a.rating;
        case 'created':
          return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
        default:
          return 0;
      }
    });

  const getCategoryColor = (category: string) => {
    const colors = {
      Safari: 'bg-green-100 text-green-800',
      Cultural: 'bg-purple-100 text-purple-800',
      Adventure: 'bg-orange-100 text-orange-800',
      Photography: 'bg-blue-100 text-blue-800'
    };
    return colors[category as keyof typeof colors] || 'bg-gray-100 text-gray-800';
  };

  const handleSelectTour = (tourId: string) => {
    setSelectedTours(prev =>
      prev.includes(tourId)
        ? prev.filter(id => id !== tourId)
        : [...prev, tourId]
    );
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedTours([]);
    } else {
      setSelectedTours(sortedTours.map(tour => tour.id));
    }
    setSelectAll(!selectAll);
  };

  const handleBulkDelete = () => {
    if (selectedTours.length === 0) return;
    if (window.confirm(`Are you sure you want to delete ${selectedTours.length} tour(s)?`)) {
      onBulkDelete?.(selectedTours);
      setSelectedTours([]);
      setSelectAll(false);
    }
  };

  const handleBulkUpdateStatus = (status: 'active' | 'suspended' | 'deactivated') => {
    if (selectedTours.length === 0) return;
    const action = status === 'active' ? 'activate' : status === 'suspended' ? 'suspend' : 'deactivate';
    if (window.confirm(`Are you sure you want to ${action} ${selectedTours.length} tour(s)?`)) {
      onBulkUpdateStatus?.(selectedTours, status);
      setSelectedTours([]);
      setSelectAll(false);
    }
  };

  // Update select all state when tours change
  React.useEffect(() => {
    setSelectAll(selectedTours.length === sortedTours.length && sortedTours.length > 0);
  }, [selectedTours, sortedTours]);

  return (
    <div className="space-y-6">
      {/* Filters and Controls */}
      <div className={`rounded-2xl shadow-modern backdrop-blur-sm border p-6 transition-all duration-300 ${actualTheme === 'dark'
        ? 'bg-gradient-to-br from-slate-800/90 to-slate-700/90 border-slate-600/30'
        : 'bg-white/90 border-neutral-200/50'
        }`}>
        <div className="flex flex-col space-y-4">
          {/* Top Row - Filters and Info - Unified Horizontal Scroll for Mobile */}
          <div className="flex flex-col space-y-5">
            <div className="flex items-center justify-between">
              {/* Select All Checkbox */}
              <div className="flex items-center">
                <button
                  onClick={handleSelectAll}
                  className={`w-6 h-6 rounded-lg border-2 flex items-center justify-center transition-all duration-300 shadow-sm ${selectAll
                    ? 'bg-primary-600 border-primary-600 text-white'
                    : actualTheme === 'dark'
                      ? 'bg-slate-700 border-slate-500 text-slate-500'
                      : 'bg-white border-neutral-300 text-gray-400'
                    }`}
                >
                  {selectAll && <CheckIcon className="h-4 w-4" />}
                </button>
                <label className={`ml-3 text-sm font-bold transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-200' : 'text-gray-800'}`}>
                  Select All
                </label>
              </div>
              
              <div className={`hidden sm:block text-sm font-medium transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-400' : 'text-gray-500'}`}>
                Showing {sortedTours.length} of {tours.length} tours
              </div>
            </div>

            {/* Scrollable Filters Container */}
            <div className="relative group -mx-2 sm:mx-0">
              <div className="flex flex-nowrap-forced overflow-x-auto scrollbar-hide items-center gap-3 pb-2 px-2" style={{ flexWrap: 'nowrap' }}>
                {/* Status Filter */}
                <div className={`relative flex-shrink-0 flex items-center rounded-2xl border shadow-sm transition-all duration-300 ${
                  actualTheme === 'dark' ? 'bg-slate-700 border-slate-600' : 'bg-white border-gray-200'
                }`}>
                  <select
                    value={filter}
                    onChange={(e) => setFilter(e.target.value)}
                    className="appearance-none bg-transparent py-2.5 pl-4 pr-10 outline-none text-sm font-semibold rounded-2xl cursor-pointer"
                  >
                    <option value="all">All Status</option>
                    <option value="active">Active</option>
                    <option value="scheduled">Scheduled</option>
                    <option value="suspended">Suspended</option>
                    <option value="deactivated">Deact.</option>
                    <option value="expired">Expired</option>
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3">
                    <svg className="h-4 w-4 fill-current opacity-60" viewBox="0 0 20 20"><path d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" /></svg>
                  </div>
                </div>

                {/* Sort */}
                <div className={`relative flex-shrink-0 flex items-center rounded-2xl border shadow-sm transition-all duration-300 ${
                  actualTheme === 'dark' ? 'bg-slate-700 border-slate-600' : 'bg-white border-gray-200'
                }`}>
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="appearance-none bg-transparent py-2.5 pl-4 pr-10 outline-none text-sm font-semibold rounded-2xl cursor-pointer"
                  >
                    <option value="featured">Featured Order</option>
                    <option value="title">Alphabetical</option>
                    <option value="price">Highest Price</option>
                    <option value="rating">Top Rated</option>
                    <option value="created">Newest First</option>
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3">
                    <svg className="h-4 w-4 fill-current opacity-60" viewBox="0 0 20 20"><path d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" /></svg>
                  </div>
                </div>

                {/* Pricing Tier Filter */}
                <div className={`flex flex-shrink-0 items-center p-1 rounded-2xl border shadow-sm ${
                  actualTheme === 'dark' ? 'bg-slate-800 border-slate-700' : 'bg-neutral-100 border-gray-200'
                }`}>
                  {Object.values(PricingTier).map((t) => (
                    <button
                      key={t}
                      onClick={() => setTier(t)}
                      className={`px-4 py-1.5 rounded-xl text-xs sm:text-sm font-bold transition-all duration-300 ${tier === t
                        ? 'bg-white dark:bg-slate-600 text-primary-700 dark:text-primary-300 shadow-sm'
                        : 'text-gray-500 hover:text-gray-800 dark:text-slate-400 dark:hover:text-slate-200'
                        }`}
                    >
                      {t.charAt(0) + t.slice(1).toLowerCase()}
                    </button>
                  ))}
                </div>

                {/* Currency Selector */}
                <div className={`relative flex-shrink-0 flex items-center rounded-2xl border shadow-sm pr-3 ${
                  actualTheme === 'dark' ? 'bg-slate-700 border-slate-600' : 'bg-white border-gray-200'
                }`}>
                  <div className="pl-3 text-[10px] uppercase font-black tracking-widest opacity-40">Cur:</div>
                  <select
                    value={displayCurrency}
                    onChange={(e) => setCurrency(e.target.value)}
                    className="appearance-none bg-transparent py-2.5 pl-1 pr-6 outline-none text-sm font-bold cursor-pointer"
                  >
                    {currencies.map(c => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2">
                    <svg className="h-4 w-4 fill-current opacity-60" viewBox="0 0 20 20"><path d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" /></svg>
                  </div>
                </div>
              </div>
              
              {/* Fade cues for mobile scroll */}
              <div className={`absolute right-0 top-0 bottom-2 w-12 bg-gradient-to-l pointer-events-none sm:hidden z-20 ${
                actualTheme === 'dark' ? 'from-slate-800 via-slate-800/80 to-transparent' : 'from-white via-white/80 to-transparent'
              }`} />
            </div>

            <div className={`sm:hidden text-xs font-medium transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-500' : 'text-gray-500'}`}>
              Showing {sortedTours.length} of {tours.length} tours
            </div>
          </div>

          {/* Bulk Actions - Responsive Layout */}
          {selectedTours.length > 0 && (
            <div className={`border-t pt-5 transition-colors duration-300 ${actualTheme === 'dark' ? 'border-slate-700' : 'border-neutral-200'}`}>
              <div className="flex flex-col space-y-4">
                <div className="flex items-center justify-between">
                  <div className={`text-sm font-bold transition-colors duration-300 ${actualTheme === 'dark' ? 'text-blue-400' : 'text-primary-700'}`}>
                    {selectedTours.length} tour(s) selected
                  </div>
                  <button 
                    onClick={() => setSelectedTours([])}
                    className="text-xs font-bold text-gray-500 hover:text-red-500 transition-colors uppercase tracking-widest"
                  >
                    Clear Selection
                  </button>
                </div>
                
                <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-2">
                  <button
                    onClick={() => handleBulkUpdateStatus('active')}
                    className="flex items-center justify-center px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-[11px] font-black uppercase tracking-widest rounded-xl transition-all active:scale-95 shadow-md shadow-green-600/10"
                  >
                    <CheckIcon className="h-4 w-4 mr-2" />
                    Activate
                  </button>
                  <button
                    onClick={() => handleBulkUpdateStatus('suspended')}
                    className="flex items-center justify-center px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white text-[11px] font-black uppercase tracking-widest rounded-xl transition-all active:scale-95 shadow-md shadow-amber-500/10"
                  >
                    <EyeSlashIcon className="h-4 w-4 mr-2" />
                    Suspend
                  </button>
                  <button
                    onClick={() => handleBulkUpdateStatus('deactivated')}
                    className="flex items-center justify-center px-4 py-2 bg-slate-600 hover:bg-slate-700 text-white text-[11px] font-black uppercase tracking-widest rounded-xl transition-all active:scale-95 shadow-md shadow-slate-600/10"
                  >
                    <EyeSlashIcon className="h-4 w-4 mr-2" />
                    Deactivate
                  </button>
                  <button
                    onClick={handleBulkDelete}
                    className="flex items-center justify-center px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-[11px] font-black uppercase tracking-widest rounded-xl transition-all active:scale-95 shadow-md shadow-red-600/10 col-span-2 sm:col-span-1"
                  >
                    <TrashIcon className="h-4 w-4 mr-2" />
                    Delete Permanent
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Tours Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
        {sortedTours.map((tour) => (
          <div key={tour.id} className={`rounded-2xl backdrop-blur-sm border overflow-hidden transition-all duration-300 floating-card hover:floating-card-hover ${actualTheme === 'dark'
            ? 'bg-gradient-to-br from-slate-800/90 to-slate-700/90 border-slate-600/30'
            : 'bg-white/90 border-neutral-200/50'
            } ${selectedTours.includes(tour.id) ? 'ring-2 ring-primary-500' : ''}`}>
            {/* Selection Checkbox */}
            <div className="absolute top-3 left-3 z-10">
              <button
                onClick={() => handleSelectTour(tour.id)}
                className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all duration-300 ${selectedTours.includes(tour.id)
                  ? 'bg-primary-600 border-primary-600 text-white'
                  : actualTheme === 'dark'
                    ? 'bg-slate-800/80 border-slate-400 text-slate-400'
                    : 'bg-white/80 border-gray-400 text-gray-400'
                  }`}
              >
                {selectedTours.includes(tour.id) && (
                  <CheckIcon className="h-4 w-4" />
                )}
              </button>
            </div>

            {/* Image */}
            <div className="relative h-48 bg-gray-200">
              <img
                src={tour.images && tour.images.length > 0 ? tour.images[0] : 'https://images.unsplash.com/photo-1516026672322-bc52d61a55d5?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80'}
                alt={tour.title}
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                }}
              />
              <div className="absolute top-3 left-12">
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getCategoryColor(tour.category)}`}>
                  {tour.category}
                </span>
              </div>
              <div className="absolute top-3 right-3">
                <div className={`flex items-center rounded-full px-2 py-1 backdrop-blur-sm ${actualTheme === 'dark' ? 'bg-slate-800/80' : 'bg-white/80'
                  }`}>
                  <StarIcon className="h-4 w-4 text-yellow-400 mr-1" />
                  <span className={`text-sm font-medium ${actualTheme === 'dark' ? 'text-slate-200' : 'text-gray-900'
                    }`}>{tour.rating}</span>
                </div>
              </div>
              {tour.status === 'deactivated' && (
                <div className="absolute inset-0 bg-black bg-opacity-75 flex items-center justify-center">
                  <span className="text-white font-medium text-lg flex items-center"><EyeSlashIcon className="w-5 h-5 mr-2" /> Deactivated</span>
                </div>
              )}
              {tour.status === 'suspended' && (
                <div className="absolute inset-0 bg-white bg-opacity-50 backdrop-blur-sm flex items-center justify-center border-4 border-yellow-500/50">
                  <span className="text-yellow-800 font-bold px-4 py-2 bg-yellow-100 rounded-full shadow-lg">Suspended</span>
                </div>
              )}
              {tour.status === 'scheduled' && (
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent p-4 pb-2 pt-12">
                  <span className="text-white text-xs font-medium bg-blue-500/80 px-2 py-1 rounded backdrop-blur-md">Scheduled</span>
                </div>
              )}
              {tour.status === 'expired' && (
                <div className="absolute inset-0 bg-gray-500 bg-opacity-40 grayscale flex items-center justify-center">
                  <span className="text-white font-bold tracking-widest uppercase bg-black/50 px-3 py-1 rounded">Expired</span>
                </div>
              )}
            </div>

            {/* Content */}
            <div className="p-6">
              <div className="flex items-start justify-between mb-3">
                <h3 className={`text-lg font-semibold line-clamp-2 transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-200' : 'text-gray-900'
                  }`}>{tour.title}</h3>
                <div className="flex items-center space-x-1 ml-2">
                  <button
                    onClick={() => onEdit(tour)}
                    className={`p-1 rounded-modern transition-all duration-300 hover-magnify-sm ${actualTheme === 'dark'
                      ? 'text-slate-400 hover:text-primary-400 hover:bg-slate-700'
                      : 'text-gray-400 hover:text-primary-600 hover:bg-gray-100'
                      }`}
                    title="Edit tour"
                  >
                    <PencilIcon className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => onDelete(tour.id)}
                    className={`p-1 rounded-modern transition-all duration-300 hover-magnify-sm ${actualTheme === 'dark'
                      ? 'text-slate-400 hover:text-red-400 hover:bg-slate-700'
                      : 'text-gray-400 hover:text-red-600 hover:bg-gray-100'
                      }`}
                    title="Delete tour"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
              </div>

              <p className={`text-sm mb-4 line-clamp-2 transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-400' : 'text-gray-600'
                }`}>{tour.description}</p>

              {/* Tour Details */}
              <div className="space-y-2 mb-4">
                <div className={`flex items-center text-sm transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-400' : 'text-gray-500'
                  }`}>
                  <MapPinIcon className="h-4 w-4 mr-2" />
                  <span>{tour.location?.name || 'Location not specified'}</span>
                </div>
                <div className={`flex items-center text-sm transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-400' : 'text-gray-500'
                  }`}>
                  <ClockIcon className="h-4 w-4 mr-2" />
                  <span>{tour.duration}</span>
                </div>
                <div className={`flex items-center text-sm transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-400' : 'text-gray-500'
                  }`}>
                  <UsersIcon className="h-4 w-4 mr-2" />
                  <span>Max {tour.maxParticipants} people</span>
                </div>
              </div>

              {/* Highlights */}
              {tour.highlights && tour.highlights.length > 0 && (
                <div className="mb-4">
                  <div className="flex flex-wrap gap-1">
                    {tour.highlights.slice(0, 3).map((highlight: string, index: number) => (
                      <span
                        key={index}
                        className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium transition-colors duration-300 ${actualTheme === 'dark'
                          ? 'bg-slate-700 text-slate-300'
                          : 'bg-gray-100 text-gray-800'
                          }`}
                      >
                        {highlight}
                      </span>
                    ))}
                    {tour.highlights.length > 3 && (
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium transition-colors duration-300 ${actualTheme === 'dark'
                        ? 'bg-slate-700 text-slate-300'
                        : 'bg-gray-100 text-gray-800'
                        }`}>
                        +{tour.highlights.length - 3} more
                      </span>
                    )}
                  </div>
                </div>
              )}

              {/* Price and Rating */}
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <CurrencyDollarIcon className="h-4 w-4 text-green-600 mr-1" />
                  <span className={`text-lg font-bold transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-200' : 'text-gray-900'
                    }`}>
                    {(() => {
                      // Use the normalized price as the starting point
                      let amount = (tour as any).price || 0;
                      
                      // If a specific tier is selected, try to get that specific tier price
                      if (tour.tierVariants && (tour.tierVariants as any)[tier]) {
                        const v = (tour.tierVariants as any)[tier];
                        // Check both possible structures: { international: { adult: X } } OR { baseServiceCosts: { INTERNATIONAL: { adult: X } } }
                        const local = v.local || v.baseServiceCosts?.LOCAL || v.baseServiceCosts?.local || {};
                        const international = v.international || v.baseServiceCosts?.INTERNATIONAL || v.baseServiceCosts?.international || {};
                        
                        const localAdult = local.adult || local.adultBase || 0;
                        const intlAdult = international.adult || international.adultBase || 0;
                        
                        if (localAdult > 0 || intlAdult > 0) {
                          // Default to local if available, otherwise international
                          amount = localAdult > 0 ? localAdult : intlAdult;
                        }
                      }

                      // If amount is still 0, try to find ANY price in variants
                      if (amount === 0 && tour.tierVariants) {
                        const findAny = (obj: any) => {
                          for (const k in obj) {
                            const v = obj[k];
                            const rates = v.local || v.international || v.baseServiceCosts?.LOCAL || v.baseServiceCosts?.local || v.baseServiceCosts?.INTERNATIONAL || {};
                            if (rates.adult || rates.adultBase) return rates.adult || rates.adultBase;
                          }
                          return 0;
                        };
                        amount = findAny(tour.tierVariants);
                      }

                      // Convert if needed
                      let displayAmount = amount;
                      const isKes = displayCurrency === 'KES';
                      
                      if (isKes && amount < 1000 && exchangeRates?.KES) {
                        displayAmount = amount * exchangeRates.KES;
                      } else if (!isKes && amount > 1000 && exchangeRates?.KES) {
                        displayAmount = amount / exchangeRates.KES;
                      }

                      return (
                        <>
                          <span className="text-xs font-normal text-gray-500 mr-1 italic">Starts from</span>
                          {currencyService.formatCurrency(displayAmount, displayCurrency)}
                        </>
                      );
                    })()}
                  </span>
                  {tour.tierVariants && Object.keys(tour.tierVariants).length > 0 && (
                    <span className={`text-sm ml-1 transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-400' : 'text-gray-500'
                      }`}>per adult</span>
                  )}
                </div>
                <div className={`flex items-center text-sm transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-400' : 'text-gray-500'
                  }`}>
                  <StarIcon className="h-4 w-4 text-yellow-400 mr-1" />
                  <span>{tour.rating}</span>
                  <span className="ml-1">({tour.reviewCount})</span>
                </div>
              </div>

              {/* Status */}
              <div className={`mt-4 pt-4 border-t transition-colors duration-300 ${actualTheme === 'dark' ? 'border-slate-600/30' : 'border-gray-200'
                }`}>
                <div className="flex items-center justify-between mt-2">
                  <div className="flex items-center">
                    {tour.status === 'active' && (
                      <div className="flex items-center text-green-600">
                        <CheckIcon className="h-4 w-4 mr-1" />
                        <span className="text-sm font-medium">Active</span>
                      </div>
                    )}
                    {tour.status === 'scheduled' && (
                      <div className="flex items-center text-blue-600">
                        <ClockIcon className="h-4 w-4 mr-1" />
                        <span className="text-sm font-medium">Scheduled</span>
                      </div>
                    )}
                    {tour.status === 'suspended' && (
                      <div className="flex items-center text-yellow-600">
                        <EyeSlashIcon className="h-4 w-4 mr-1" />
                        <span className="text-sm font-medium">Suspended</span>
                      </div>
                    )}
                    {(tour.status === 'deactivated' || !tour.status) && (
                      <div className="flex items-center text-gray-500">
                        <EyeSlashIcon className="h-4 w-4 mr-1" />
                        <span className="text-sm font-medium">Deactivated</span>
                      </div>
                    )}
                    {tour.status === 'expired' && (
                      <div className="flex items-center text-red-500">
                        <ClockIcon className="h-4 w-4 mr-1" />
                        <span className="text-sm font-medium">Expired</span>
                      </div>
                    )}
                  </div>
                  <div className={`text-xs transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-500' : 'text-gray-500'
                    }`}>
                    Updated {tour.updatedAt ? new Date(tour.updatedAt).toLocaleDateString() : tour.createdAt ? new Date(tour.createdAt).toLocaleDateString() : 'N/A'}
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Empty State */}
      {sortedTours.length === 0 && (
        <div className={`rounded-2xl shadow-modern backdrop-blur-sm border p-12 text-center transition-all duration-300 ${actualTheme === 'dark'
          ? 'bg-gradient-to-br from-slate-800/90 to-slate-700/90 border-slate-600/30'
          : 'bg-white/90 border-neutral-200/50'
          }`}>
          <div className={`mx-auto h-12 w-12 transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-500' : 'text-gray-400'
            }`}>
            <TagIcon className="h-12 w-12" />
          </div>
          <h3 className={`mt-4 text-lg font-medium transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-200' : 'text-gray-900'
            }`}>No tours found</h3>
          <p className={`mt-2 transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-400' : 'text-gray-500'
            }`}>
            {filter === 'all'
              ? 'Get started by creating your first tour.'
              : `No ${filter} tours found. Try adjusting your filters.`
            }
          </p>
        </div>
      )}
    </div>
  );
};

export default TourList;
